import {
    NgModule,
    APP_INITIALIZER,
    CUSTOM_ELEMENTS_SCHEMA,
    Injector,
    ErrorHandler
} from '@angular/core';
import { APP_BASE_HREF, CommonModule } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClientModule, HttpClientXsrfModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouteReuseStrategy } from '@angular/router';
import { DatePipe } from '@angular/common';

import { mockBackendProvider } from './shared/helpers'; // used to create mock backend
import { HttpJwtInterceptor } from './shared/helpers';
import { TSRouteReuseStrategy } from '@/shared/helpers';

import { HideProdDirective } from '@/shared/directives';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LanguageTranslationModule } from './modules/language-translation/language-translation.module';

import { AppRoutingModule } from './app-routing.module';
import { AppConfig } from './app.config';
import { AppComponent } from './app.component';

import { environment } from 'src/environments/environment';
import { AuthGuard } from './auth';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { IAppConfig } from './shared/models';
import { GlobalErrorHandler } from './shared/helpers';

import { LoggerModule } from 'ngx-logger';
import { FormsModule } from '@angular/forms';
import { ImageCropperModule } from 'ngx-image-cropper';
import { NgSelectModule } from '@ng-select/ng-select';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';

import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';

const appInitializerFn = (appConfig: AppConfig) => {
    return () => appConfig.loadAppConfig();
};

const providers = [];
providers.push({ provide: APP_BASE_HREF, useValue: '/ng' });
providers.push(AuthGuard);
providers.push({ provide: HTTP_INTERCEPTORS, useClass: HttpJwtInterceptor, multi: true });
providers.push(AppConfig);
providers.push({
    provide: APP_INITIALIZER,
    useFactory: appInitializerFn,
    multi: true,
    deps: [AppConfig]
});
providers.push({ provide: RouteReuseStrategy, useClass: TSRouteReuseStrategy });
providers.push({ provide: ErrorHandler, useClass: GlobalErrorHandler });
providers.push(DatePipe);
providers.push(provideFirebaseApp(() => initializeApp(environment.firebase)));
providers.push(provideMessaging(() => getMessaging()));

if ((environment as IAppConfig).mock) {
    providers.push(mockBackendProvider); // provider used to create mock backend
}

@NgModule({
    declarations: [AppComponent, HideProdDirective],
    imports: [
        CommonModule,
        BrowserModule,
        BrowserAnimationsModule,
        HttpClientModule,
        HttpClientXsrfModule.withOptions({
            cookieName: 'csrftoken',
            headerName: 'X-CSRFToken'
        }),
        LanguageTranslationModule,
        AppRoutingModule,
        NgbModule,
        ImageCropperModule,
        LoggerModule.forRoot({
            // Log Levels: "TRACE=0, DEBUG=1, INFO=2, LOG=3, WARN=4, ERROR=5, FATAL=6, OFF=7"
            level: environment.logLevel,
            serverLogLevel: environment.serverLogLevel,
            serverLoggingUrl: (environment as IAppConfig).serverLogUrl,
            timestampFormat: 'HH:mm:ss.SSS',
            disableConsoleLogging: environment.production ? true : false,
            // colorScheme: ['purple', 'teal', 'gray', 'gray', 'red', 'red', 'red'],
            enableSourceMaps: environment.production ? false : true
        }),
        FormsModule,
        NgSelectModule,
        MatSelectModule
    ],
    providers, // Same as providers: providers.
    // Allows NgModule to contain Non-Angular elements & Element properties named with dash case (-)
    schemas: [CUSTOM_ELEMENTS_SCHEMA], // Need for ngx-spinner
    bootstrap: [AppComponent]
})
export class AppModule {
    static injector: Injector;
    constructor(injector: Injector) {
        AppModule.injector = injector;
    }
}
